import { HTMLAttributes } from 'react';
import { Node } from 'slate';
import { slateToHtml, slateToHtmlConfig } from '@slate-serializers/html';
import { type SlateToDomConfig } from '@slate-serializers/dom';
import { Element as DOMElement } from 'domhandler';
import { classNames } from 'primereact/utils';
import { ColumnsRatio } from '../Elements/Columns/Columns';
import { ImageElement, LinkElement, SIZE_MAP, VideoElement } from '../types';

export const toHtml = (nodes: Node[]) => {
	const config: SlateToDomConfig = {
		...slateToHtmlConfig,
		markMap: {
			...slateToHtmlConfig.markMap,
		},
		markTransforms: {
			...slateToHtmlConfig.markTransforms,
			// @ts-ignore
			fontSize: ({ node, children }) => {
				const fontSize = node.fontSize as keyof typeof SIZE_MAP;
				const size = SIZE_MAP[fontSize];
				if (fontSize && size) {
					return new DOMElement('span', { style: `font-size: ${size}` }, children);
				}
				return children;
			},
			// @ts-ignore
			color: ({ node, children }) => {
				if (node.color) {
					return new DOMElement('span', { style: `color: ${node.color}` }, children);
				}
				return children;
			},
			// @ts-ignore
			backgroundColor: ({ node, children }) => {
				if (node.backgroundColor) {
					return new DOMElement('span', { style: `background-color: ${node.backgroundColor}` }, children);
				}
				return children;
			},
		},
		elementMap: {
			...slateToHtmlConfig.elementMap,
			headingOne: 'h1',
			headingTwo: 'h2',
			headingThree: 'h3',
			image: 'img',
			orderedList: 'ol',
			unorderedList: 'ul',
			listItem: 'li',
			link: 'a',
			blockquote: 'blockquote',
		},
		elementTransforms: {
			...slateToHtmlConfig.elementTransforms,
			alignLeft: ({ children }) => {
				return new DOMElement('div', { class: 'text-left' }, children);
			},
			alignCenter: ({ children }) => {
				return new DOMElement('div', { class: 'text-center' }, children);
			},
			alignRight: ({ children }) => {
				return new DOMElement('div', { class: 'text-right' }, children);
			},
			image: ({ node }) => {
				const element = node as ImageElement;
				const attrs: any = {
					src: element.url,
					alt: element.alt,
					style: `width: 800px;`,
				};
				if (element.width === 'full' || element.width === '100%') {
					attrs.class = 'w-full';
				} else if (!Number.isNaN(Number(element.width))) {
					attrs.style = `width: ${element.width}${element.widthUnit};`;
				}
				attrs.class = classNames('embed-image', {
					'float-left': element.float === 'left',
					'float-right': element.float === 'right',
				});
				return new DOMElement('img', attrs);
			},
			video: ({ node }) => {
				const element = node as VideoElement;
				const attrs: any = {
					src: element.url,
					alt: element.alt,
				};
				const wrapperAttrs: any = {
					style: `width: 400px;`,
				};
				if (element.width === 'full' || element.width === '100%') {
					wrapperAttrs.class = 'w-full';
				} else if (!Number.isNaN(Number(element.width))) {
					wrapperAttrs.style = `width: ${element.width}${element.widthUnit};`;
				}
				return new DOMElement(
					'div',
					{
						...wrapperAttrs,
						class: classNames(
							'embed-video-wrapper',
							{ 'float-left': element.float === 'left', 'float-right': element.float === 'right' },
							wrapperAttrs.class || undefined
						),
					},
					[new DOMElement('iframe', attrs)]
				);
			},
			twoColumns: ({ node, attribs, children }) => {
				const ratio = (node?.ratio || ColumnsRatio.ONE_TO_TWO) as ColumnsRatio;

				if (Array.isArray(children) && children.length >= 2) {
					return new DOMElement(
						'div',
						{
							class: 'flex flex-row gap-2',
							'data-ratio': ratio,
						},
						[
							new DOMElement(
								'div',
								{
									class:
										classNames('column', {
											'flex-1': [ColumnsRatio.ONE_TO_TWO, ColumnsRatio.ONE_TO_ONE].includes(
												ratio
											),
											'flex-2': [ColumnsRatio.TWO_TO_ONE].includes(ratio),
										}) || '',
								},
								[children[0]]
							),
							new DOMElement(
								'div',
								{
									class:
										classNames('column', {
											'flex-1': [ColumnsRatio.ONE_TO_ONE, ColumnsRatio.TWO_TO_ONE].includes(
												ratio
											),
											'flex-2': [ColumnsRatio.ONE_TO_TWO].includes(ratio),
										}) || '',
								},
								[children[1]]
							),
						]
					);
				}

				return undefined;
			},
			table: ({ children }) => {
				return new DOMElement('table', {}, children);
			},
			tableRow: ({ children }) => {
				return new DOMElement('tr', {}, children);
			},
			tableCell: ({ children }) => {
				return new DOMElement('td', {}, children);
			},
			link: ({ node, children }) => {
				const element = node as LinkElement;
				const attrs: any = {
					href: element.url,
				};
				if (element.openInNewTab) {
					attrs.target = '_blank';
				}
				return new DOMElement('a', attrs, children);
			},
			floatLeft: ({ children }) => {
				return new DOMElement('div', { class: 'float-left' }, children);
			},
			floatRight: ({ children }) => {
				return new DOMElement('div', { class: 'float-right' }, children);
			},
			clearBoth: ({ children }) => {
				return new DOMElement('div', { class: 'clear-both' }, children);
			},
		},
		convertLineBreakToBr: true,
	};

	return slateToHtml(nodes, config);
};
